import {
  connectRealtime,
  disconnectRealtime,
  isRealtimeActive,
  onRealtimeEvent,
} from '@/shared/api';
import { authApi } from '@/entities/Session/@x/ReminderHistory';
import { useReminderHistoryStore } from './reminder-history.store';

export const NOTICE_NEW_EVENT = 'notice:new';

const RECONNECT_DELAY_MS = 5000;

let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let unsubscribers: Array<() => void> = [];

function clearReconnectTimer(): void {
  if (reconnectTimer !== null) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

export function useRealtimeNotices() {
  const reminderHistoryStore = useReminderHistoryStore();

  async function reconcile(): Promise<void> {
    let summarySucceeded = false;

    try {
      await reminderHistoryStore.loadNoticeSummary();
      summarySucceeded = true;
    } catch {
      // list refresh failed; the count fallback below still runs
    }

    if (summarySucceeded) {
      // loadNoticeSummary already set the authoritative unread count from the
      // same countUnread source, so a separate count request would be redundant
      return;
    }

    try {
      await reminderHistoryStore.loadUnreadNoticeCount();
    } catch {
      // count refresh failed; keep the last known unread count
    }
  }

  function scheduleReconnect(): void {
    disconnectRealtime();

    if (reconnectTimer !== null) {
      return;
    }

    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      authApi
        .refreshToken()
        .catch(() => {})
        .finally(() => connect());
    }, RECONNECT_DELAY_MS);
  }

  function subscribe(): void {
    if (unsubscribers.length > 0) return;

    unsubscribers = [
      onRealtimeEvent('connect', () => {
        clearReconnectTimer();
        void reconcile();
      }),
      onRealtimeEvent(NOTICE_NEW_EVENT, () => {
        void reconcile();
      }),
      onRealtimeEvent('connect_error', () => {
        if (!isRealtimeActive()) {
          scheduleReconnect();
        }
      }),
    ];
  }

  function connect(): void {
    clearReconnectTimer();
    subscribe();

    try {
      connectRealtime();
    } catch {
      scheduleReconnect();
    }
  }

  function disconnect(): void {
    clearReconnectTimer();
    unsubscribers.forEach((unsubscribe) => unsubscribe());
    unsubscribers = [];
    disconnectRealtime();
  }

  return { connect, disconnect, reconcile };
}
